import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import './HowItWorksPage.css';

const HowItWorksPage = ({
  onHomeClick,
  onCategoriesClick,
  onProfileClick,
  onAccountSettingsClick,
  onForumClick,
  onJobsClick,
  onLoginClick,
  onSignupClick
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('job-hunting'); // 'job-hunting' or 'talent-hunting'

  const jobHuntingSteps = [
    {
      number: '1',
      icon: 'profile',
      title: 'Complete Your Profile',
      description: 'Create a comprehensive professional profile showcasing your skills, experience, and portfolio. A well-crafted profile increases your visibility and attracts quality opportunities.',
      details: [
        'Add your professional title and bio',
        'Upload a professional profile photo',
        'List your skills and expertise',
        'Add your work experience and education',
        'Upload portfolio samples or projects'
      ]
    },
    {
      number: '2',
      icon: 'search',
      title: 'Browse Job Opportunities',
      description: 'Explore a curated selection of remote job opportunities from companies worldwide. Utilize advanced filters to find positions that align with your skills and career goals.',
      details: [
        'Search jobs by category, skills, or keywords',
        'Filter by work type (full-time, part-time, contract)',
        'Filter by experience level and salary range',
        'Save jobs you\'re interested in',
        'Set up job alerts for new opportunities'
      ]
    },
    {
      number: '3',
      icon: 'apply',
      title: 'Apply to Jobs',
      description: 'Submit professional applications with tailored cover letters. Demonstrate your value proposition and alignment with each organization\'s needs.',
      details: [
        'Review job requirements carefully',
        'Customize your application for each job',
        'Attach your resume and portfolio',
        'Write a compelling cover letter',
        'Submit your application'
      ]
    },
    {
      number: '4',
      icon: 'collaborate',
      title: 'Get Hired & Work Remotely',
      description: 'Engage with employers through interviews and negotiations. Begin your remote career journey with companies that value your expertise and offer flexible work arrangements.',
      details: [
        'Receive interview invitations',
        'Communicate with employers',
        'Negotiate terms and conditions',
        'Start your remote work journey',
        'Build your professional network'
      ]
    }
  ];

  const talentHuntingSteps = [
    {
      number: '1',
      icon: 'company',
      title: 'Set Up Your Company Profile',
      description: 'Establish a comprehensive company profile that effectively communicates your brand identity, organizational culture, and unique value proposition to potential candidates.',
      details: [
        'Add company information and logo',
        'Write an engaging company description',
        'List your company values and culture',
        'Add company location and size',
        'Upload company photos and videos'
      ]
    },
    {
      number: '2',
      icon: 'post',
      title: 'Post Job Opportunities',
      description: 'Create detailed and compelling job postings that attract qualified talent. Clearly articulate requirements, responsibilities, and competitive benefits.',
      details: [
        'Write compelling job descriptions',
        'Specify required skills and experience',
        'Set salary range and benefits',
        'Choose work type and location',
        'Add application requirements'
      ]
    },
    {
      number: '3',
      icon: 'review',
      title: 'Review Applications',
      description: 'Evaluate applications from qualified candidates using our advanced filtering and sorting tools. Identify the best matches for your team\'s needs.',
      details: [
        'View candidate profiles and portfolios',
        'Review resumes and cover letters',
        'Filter applications by criteria',
        'Shortlist promising candidates',
        'Compare candidates side by side'
      ]
    },
    {
      number: '4',
      icon: 'hire',
      title: 'Hire & Collaborate',
      description: 'Connect with top talent through structured interviews and seamless communication. Build and manage your remote team with confidence.',
      details: [
        'Schedule interviews with candidates',
        'Communicate through the platform',
        'Make hiring decisions',
        'Onboard new team members',
        'Manage your remote team'
      ]
    }
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/signup');
    }
  };

  const handleCompleteProfile = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="how-it-works-page how-it-works-page-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="how-it-works-header-nav">
        <Header
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={() => {}}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={onJobsClick}
          isLoggedIn={isAuthenticated}
          user={user}
          currentPage="how-it-works"
        />
      </div>

      <div className="how-it-works-container">
        {/* Hero Section */}
        <div className="how-it-works-hero">
          <div className="hero-content">
            <h1>How It Works</h1>
            <p className="hero-subtitle">
              Get started with RemoteBirdy in just a few simple steps. Whether you're looking for opportunities or talent, we've got you covered.
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="how-it-works-tabs">
          <button
            className={`tab-button ${activeTab === 'job-hunting' ? 'active' : ''}`}
            onClick={() => setActiveTab('job-hunting')}
          >
            <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <span className="tab-text">For Job Seekers</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'talent-hunting' ? 'active' : ''}`}
            onClick={() => setActiveTab('talent-hunting')}
          >
            <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
            <span className="tab-text">For Employers</span>
          </button>
        </div>

        {/* Steps Section */}
        <div className="how-it-works-steps">
          {(activeTab === 'job-hunting' ? jobHuntingSteps : talentHuntingSteps).map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-header">
                <div className="step-number-badge">
                  {step.icon === 'profile' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                  {step.icon === 'search' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  )}
                  {step.icon === 'apply' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  )}
                  {step.icon === 'collaborate' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  )}
                  {step.icon === 'company' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  )}
                  {step.icon === 'post' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="3" y1="9" x2="21" y2="9"></line>
                      <line x1="9" y1="21" x2="9" y2="9"></line>
                    </svg>
                  )}
                  {step.icon === 'review' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  )}
                  {step.icon === 'hire' && (
                    <svg className="step-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                  )}
                </div>
                <h2 className="step-title">{step.title}</h2>
              </div>
              <p className="step-description">{step.description}</p>
              <div className="step-details">
                <h3 className="details-title">Key Actions:</h3>
                <ul className="details-list">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="detail-item">
                      <svg className="detail-check-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span className="detail-text">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="how-it-works-cta">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>
              {activeTab === 'job-hunting' 
                ? 'Create your professional profile and begin exploring remote career opportunities tailored to your expertise.'
                : 'Establish your company profile and connect with top-tier remote talent to build your team.'}
            </p>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-large" onClick={handleCompleteProfile}>
                {isAuthenticated ? 'Complete Your Profile' : 'Get Started Free'}
              </button>
              {isAuthenticated && (
                <button className="btn btn-outline btn-large" onClick={() => navigate(activeTab === 'job-hunting' ? '/vacancies' : '/my-jobs')}>
                  {activeTab === 'job-hunting' ? 'Browse Jobs' : 'Post a Job'}
                </button>
              )}
            </div>
            <div className="cta-compliance">
              <p>
                By using this platform, you agree to maintain compliance with our{' '}
                <button 
                  className="compliance-link" 
                  onClick={() => navigate('/terms-and-conditions')}
                >
                  Terms of Service
                </button>
                {' '}and{' '}
                <button 
                  className="compliance-link" 
                  onClick={() => navigate('/privacy-policy')}
                >
                  Privacy Policy
                </button>
                . Please ensure all activities comply with applicable laws and regulations.
              </p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="how-it-works-tips">
          <h2>Best Practices</h2>
          <div className="tips-grid">
            <div className="tip-card">
              <div className="tip-icon-wrapper">
                <svg className="tip-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
              </div>
              <h3>Optimize Your Profile</h3>
              <p>Ensure your profile is complete with comprehensive information and a professional photo to maximize your visibility and credibility.</p>
            </div>
            <div className="tip-card">
              <div className="tip-icon-wrapper">
                <svg className="tip-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <h3>Maintain Activity</h3>
              <p>Regularly update your profile and respond promptly to messages to demonstrate professionalism and engagement.</p>
            </div>
            <div className="tip-card">
              <div className="tip-icon-wrapper">
                <svg className="tip-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </div>
              <h3>Enable Notifications</h3>
              <p>Stay informed about important updates, new opportunities, and messages by enabling platform notifications.</p>
            </div>
            <div className="tip-card">
              <div className="tip-icon-wrapper">
                <svg className="tip-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3>Engage Proactively</h3>
              <p>Maintain clear and professional communication, demonstrating genuine interest in opportunities or candidates.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <BottomNavigationBar
        onHomeClick={onHomeClick}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onForumClick={onForumClick}
        onJobsClick={onJobsClick}
        onLoginClick={onLoginClick}
        onSignupClick={onSignupClick}
      />
    </div>
  );
};

export default HowItWorksPage;

